import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import { authApi } from '../services/api';
import { AccountInfo, StripeSubscriptionStatus, MindfulTrialStatus } from '../types/auth';
import { validateUsername } from '../utils/validation';
import Spinner from './Spinner';
import HostedInvoiceDialog from './HostedInvoiceDialog';

const Account: React.FC = () => {
    const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(''); // Only for top-level errors (e.g., account loading)

    // Username update state
    const [newUsername, setNewUsername] = useState('');
    const [usernameLoading, setUsernameLoading] = useState(false);
    const [usernameValidation, setUsernameValidation] = useState<{
        isValid: boolean;
        error?: string;
    }>({ isValid: true });
    const [usernameError, setUsernameError] = useState('');
    const [usernameSuccess, setUsernameSuccess] = useState('');

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Subscription management state
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');
    const [subscriptionSuccess, setSubscriptionSuccess] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const maxCancelReasonLength = 500;

    // Expired subscription reactivation state
    const [showReactivationModal, setShowReactivationModal] = useState(false);
    const [reactivationLoading, setReactivationLoading] = useState(false);
    const [reactivationSuccess, setReactivationSuccess] = useState(false);
    const [reactivationError, setReactivationError] = useState(false);

    // Hosted invoice dialog state
    const [hostedInvoiceData, setHostedInvoiceData] = useState<{
        url: string;
    } | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        loadAccountInfo();
    }, []);

    const loadAccountInfo = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await authApi.getAccountInfo();

            if (response.status === 'success' && response.account) {
                setAccountInfo(response.account);
                setNewUsername(response.account.username);
            } else {
                setError(response.message || 'Failed to load account information');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewUsername(value);

        // Clear error when username is changed
        setUsernameError('');
        setUsernameSuccess('');

        // Real-time validation for username using common validation function
        if (value.trim()) {
            const validation = validateUsername(value);
            setUsernameValidation(validation);
        } else {
            setUsernameValidation({ isValid: true });
        }
    };

    const handleUsernameUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accountInfo || newUsername === accountInfo.username) return;

        // Validate username before submission
        const validation = validateUsername(newUsername);
        if (!validation.isValid) {
            setUsernameError(validation.error!);
            return;
        }

        try {
            setUsernameLoading(true);
            setUsernameError('');
            setUsernameSuccess('');

            const response = await authApi.updateUsername({ name: newUsername });

            if (response.status === 'success') {
                setUsernameSuccess('Username updated successfully');
                setAccountInfo(prev => (prev ? { ...prev, username: newUsername } : null));
            } else {
                setUsernameError(response.message || 'Failed to update username');
            }
        } catch (err) {
            setUsernameError('An unexpected error occurred');
        } finally {
            setUsernameLoading(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters long');
            return;
        }

        try {
            setPasswordLoading(true);
            setPasswordError('');
            setPasswordSuccess('');

            const response = await authApi.changePassword({
                currentPassword,
                newPassword,
                confirmPassword,
            });

            if (response.status === 'success') {
                setPasswordSuccess('Password changed successfully');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordError(response.message || 'Failed to change password');
            }
        } catch (err) {
            setPasswordError('An unexpected error occurred');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCancelSubscription = () => {
        setSubscriptionError('');
        setSubscriptionSuccess('');
        setCancelReason('');
        setShowCancelModal(true);
    };

    const confirmCancelSubscription = async () => {
        try {
            setSubscriptionLoading(true);
            setSubscriptionError('');
            setSubscriptionSuccess('');

            const trimmedReason = cancelReason.trim().slice(0, maxCancelReasonLength);
            const response = await authApi.cancelSubscription({ reason: trimmedReason });

            if (response.status === 'success') {
                setSubscriptionSuccess('Subscription cancelled successfully');
                setShowCancelModal(false);
                loadAccountInfo(); // Refresh account info
            } else {
                setSubscriptionError(response.message || 'Failed to cancel subscription');
            }
        } catch (err) {
            setSubscriptionError('An unexpected error occurred');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            setSubscriptionLoading(true);
            setSubscriptionError('');
            setSubscriptionSuccess('');

            const response = await authApi.reactivateSubscription();

            if (response.status === 'success') {
                // Check if hosted invoice URL is present
                if (response.hostedInvoiceUrl) {
                    setHostedInvoiceData({
                        url: response.hostedInvoiceUrl,
                    });
                } else {
                    setSubscriptionSuccess('Subscription reactivated successfully');
                    loadAccountInfo(); // Refresh account info
                }
            } else {
                setSubscriptionError(response.message || 'Failed to reactivate subscription');
            }
        } catch (err) {
            setSubscriptionError('An unexpected error occurred');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleSignUp = () => {
        navigate('/signup-existing');
    };

    const handleBackToPuzzlePlanet = () => {
        window.location.href = `${
            import.meta.env.VITE_REACT_APP_GAME_URL
        }?t=${new Date().getTime()}`;
    };

    const handleReSubscribe = () => {
        setShowReactivationModal(false);
        setReactivationSuccess(false);
        setReactivationError(false);
        navigate('/signup-existing');
    };

    const handleUpdatePayment = async () => {
        try {
            setSubscriptionError('');
            setSubscriptionSuccess('');

            const response = await authApi.getPaymentPortalUrl();

            if (response.status === 'success' && response.portalSessionURL) {
                window.open(response.portalSessionURL, '_blank');
            } else {
                setSubscriptionError(response.message || 'Failed to open payment portal');
            }
        } catch (err) {
            setSubscriptionError('An unexpected error occurred');
        }
    };

    const getSubscriptionStatusColor = (info: AccountInfo) => {
        // Check mindfulTrialStatus first
        if (info.mindfulTrialStatus === MindfulTrialStatus.ACTIVE) {
            return 'text-white bg-green-500';
        }
        if (info.mindfulTrialStatus === MindfulTrialStatus.EXPIRED) {
            return 'text-white bg-yellow-500';
        }

        switch (info.stripeSubscriptionStatus) {
            case StripeSubscriptionStatus.ACTIVE:
                if (info.autorenew === false) {
                    return 'text-white bg-yellow-500';
                }
                return 'text-white bg-green-500';
            case StripeSubscriptionStatus.TRIALING:
                if (info.autorenew === false) {
                    return 'text-white bg-yellow-500';
                }
                return 'text-white bg-green-500';
            case StripeSubscriptionStatus.CANCELED:
            case StripeSubscriptionStatus.PAST_DUE:
            case StripeSubscriptionStatus.UNPAID:
            case StripeSubscriptionStatus.INCOMPLETE:
            case StripeSubscriptionStatus.INCOMPLETE_EXPIRED:
                return 'text-white bg-red-500';
            default:
                return 'text-white bg-gray-500';
        }
    };

    const getSubscriptionStatusText = (info: AccountInfo) => {
        // Check mindfulTrialStatus first
        if (info.mindfulTrialStatus === MindfulTrialStatus.ACTIVE) {
            return 'Trial';
        }
        if (info.mindfulTrialStatus === MindfulTrialStatus.EXPIRED) {
            return 'Trial Expired';
        }

        switch (info.stripeSubscriptionStatus) {
            case StripeSubscriptionStatus.ACTIVE:
                return 'Active';
            case StripeSubscriptionStatus.TRIALING:
                return 'Trial';
            case StripeSubscriptionStatus.PAST_DUE:
                return 'Past Due';
            case StripeSubscriptionStatus.INCOMPLETE:
                return 'Payment Incomplete';
            case StripeSubscriptionStatus.UNPAID:
                return 'Unpaid';
            case StripeSubscriptionStatus.CANCELED:
            case StripeSubscriptionStatus.INCOMPLETE_EXPIRED:
                return 'Expired';
            default:
                return 'No Subscription';
        }
    };

    const getSubscriptionDateText = (info: AccountInfo): string => {
        if (!info.subscriptionEndDate) {
            return 'No subscription';
        }

        const formattedDate = new Date(info.subscriptionEndDate).toLocaleDateString();

        // Check mindfulTrialStatus first
        if (info.stripeSubscriptionStatus === StripeSubscriptionStatus.NONE) {
            switch (info.mindfulTrialStatus) {
                case MindfulTrialStatus.NONE:
                    return 'No subscription';
                case MindfulTrialStatus.ACTIVE:
                    return `Trial expires on ${formattedDate}`;
                case MindfulTrialStatus.EXPIRED:
                    return 'Trial has expired';
            }
        }

        switch (info.stripeSubscriptionStatus) {
            case StripeSubscriptionStatus.TRIALING:
                if (info.autorenew) {
                    return `Trial will convert to a subscription on ${formattedDate}, and you will be charged $3 monthly until you cancel.`;
                } else {
                    return `Trial expires on ${formattedDate}, and you will not be charged.`;
                }
            case StripeSubscriptionStatus.ACTIVE:
                if (info.autorenew) {
                    return `Renews on ${formattedDate}`;
                } else {
                    return `Expires on ${formattedDate}`;
                }
            case StripeSubscriptionStatus.CANCELED:
            case StripeSubscriptionStatus.PAST_DUE:
            case StripeSubscriptionStatus.UNPAID:
            case StripeSubscriptionStatus.INCOMPLETE:
            case StripeSubscriptionStatus.INCOMPLETE_EXPIRED:
                return `Expired at ${formattedDate}`;
            default:
                return `No subscription`;
        }
    };

    const getDaysUntilSubscriptionEnds = (endDate: string): number => {
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const renderSubscriptionEndMessage = () => {
        if (
            !accountInfo ||
            accountInfo.autorenew ||
            (accountInfo.stripeSubscriptionStatus !== StripeSubscriptionStatus.ACTIVE &&
                accountInfo.stripeSubscriptionStatus !== StripeSubscriptionStatus.TRIALING) ||
            !accountInfo.subscriptionEndDate
        ) {
            return null;
        }

        const daysRemaining = getDaysUntilSubscriptionEnds(accountInfo.subscriptionEndDate);
        return (
            <div className='mt-6 pt-6 border-t border-gray-200'>
                <p className='text-sm text-gray-600 text-center'>
                    Your subscription will end in{' '}
                    <span className='font-semibold text-gray-900'>
                        {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
                    </span>
                    . Reactivate to keep your membership.
                </p>
            </div>
        );
    };

    // Show hosted invoice dialog if needed
    if (hostedInvoiceData) {
        return <HostedInvoiceDialog hostedInvoiceData={hostedInvoiceData} />;
    }

    if (loading) {
        return (
            <div
                className='min-h-screen flex items-center justify-center'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='flex flex-col items-center'>
                    <Spinner size='lg' color='blue' />
                    <p className='mt-4 text-white'>Loading account information...</p>
                </div>
            </div>
        );
    }

    // Show error message at the top if there's an error and no account info
    const showTopError = !accountInfo && error;

    return (
        <div
            className='min-h-screen py-8'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            {/* Sticky Header */}
            <div className='sticky top-0 z-40 -mt-8 mb-8'>
                <div className='bg-black/95 border-b border-gray-800'>
                    <div className='max-w-4xl mx-auto px-4 py-3 flex items-center justify-between'>
                        <button
                            onClick={() =>
                                (window.location.href = `${
                                    import.meta.env.VITE_REACT_APP_GAME_URL
                                }?t=${new Date().getTime()}`)
                            }
                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-2 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                        >
                            ← Back to Puzzle Planet
                        </button>
                        <span className='text-sm text-gray-300'>Account</span>
                    </div>
                </div>
            </div>

            <div className='max-w-4xl mx-auto px-4'>
                {/* Header */}
                <div className='text-center mb-8'>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Account Settings</h1>
                    <p className='text-lg text-gray-400'>Manage your account and subscription</p>
                </div>

                {/* Top-level error message (only for account loading errors) */}
                {showTopError && (
                    <div className='mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                                <svg
                                    className='w-5 h-5 mr-2'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                    ></path>
                                </svg>
                                {error}
                            </div>
                            <button
                                onClick={loadAccountInfo}
                                className='ml-4 bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium text-sm'
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {/* Subscription Management */}
                <div className='mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-8'>
                    <h2 className='text-2xl font-semibold text-gray-900 mb-6'>Subscription</h2>

                    {/* Subscription success/error messages */}
                    {subscriptionSuccess && (
                        <div className='mb-6'>
                            <div className='p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg'>
                                <div className='flex items-center'>
                                    <svg
                                        className='w-5 h-5 mr-2'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M5 13l4 4L19 7'
                                        ></path>
                                    </svg>
                                    {subscriptionSuccess}
                                </div>
                            </div>
                        </div>
                    )}

                    {subscriptionError && (
                        <div className='mb-6'>
                            <div className='p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                                <div className='flex items-center'>
                                    <svg
                                        className='w-5 h-5 mr-2'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                        ></path>
                                    </svg>
                                    {subscriptionError}
                                </div>
                            </div>
                        </div>
                    )}

                    {accountInfo ? (
                        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
                            <div className='flex items-center space-x-4'>
                                <div
                                    className={`px-4 py-2 rounded-full text-sm font-medium ${getSubscriptionStatusColor(
                                        accountInfo
                                    )}`}
                                >
                                    {getSubscriptionStatusText(accountInfo)}
                                </div>
                                {(accountInfo.subscriptionEndDate ||
                                    accountInfo.mindfulTrialStatus === MindfulTrialStatus.ACTIVE ||
                                    accountInfo.mindfulTrialStatus ===
                                        MindfulTrialStatus.EXPIRED) && (
                                    <span className='text-sm text-gray-600'>
                                        {getSubscriptionDateText(accountInfo)}
                                    </span>
                                )}
                            </div>

                            <div className='flex flex-wrap gap-3'>
                                {/* NO SUBSCRIPTION - show signup button */}
                                {accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.NONE &&
                                    accountInfo.mindfulTrialStatus === MindfulTrialStatus.NONE && (
                                        <button
                                            onClick={handleSignUp}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Signup
                                        </button>
                                    )}

                                {/* MINDFUL TRIAL ACTIVE - no stripe subscription show trial state with no buttons */}
                                {accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.NONE &&
                                    accountInfo.mindfulTrialStatus === MindfulTrialStatus.ACTIVE &&
                                    null}

                                {/* MINDFUL TRIAL EXPIRED - no stripe subscription show signup button */}
                                {accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.NONE &&
                                    accountInfo.mindfulTrialStatus ===
                                        MindfulTrialStatus.EXPIRED && (
                                        <button
                                            onClick={handleSignUp}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Signup
                                        </button>
                                    )}

                                {/* Stripe Trial or stripe active*/}
                                {(accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.TRIALING ||
                                    accountInfo.stripeSubscriptionStatus ===
                                        StripeSubscriptionStatus.ACTIVE) && (
                                    <>
                                        <button
                                            onClick={handleUpdatePayment}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Manage Payment
                                        </button>
                                        {accountInfo.autorenew ? (
                                            <button
                                                onClick={handleCancelSubscription}
                                                disabled={subscriptionLoading}
                                                className='bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                            >
                                                {subscriptionLoading ? (
                                                    <div className='flex items-center'>
                                                        <Spinner
                                                            size='sm'
                                                            color='black'
                                                            className='mr-2'
                                                        />
                                                        Cancelling...
                                                    </div>
                                                ) : (
                                                    'Cancel Subscription'
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleReactivateSubscription}
                                                disabled={subscriptionLoading}
                                                className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                            >
                                                {subscriptionLoading ? (
                                                    <div className='flex items-center'>
                                                        <Spinner
                                                            size='sm'
                                                            color='black'
                                                            className='mr-2'
                                                        />
                                                        Reactivating...
                                                    </div>
                                                ) : (
                                                    'Reactivate'
                                                )}
                                            </button>
                                        )}
                                    </>
                                )}

                                {/* PAST_DUE, UNPAID, INCOMPLETE */}
                                {(accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.PAST_DUE ||
                                    accountInfo.stripeSubscriptionStatus ===
                                        StripeSubscriptionStatus.UNPAID ||
                                    accountInfo.stripeSubscriptionStatus ===
                                        StripeSubscriptionStatus.INCOMPLETE) && (
                                    <button
                                        onClick={handleUpdatePayment}
                                        className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                    >
                                        Manage Payment
                                    </button>
                                )}

                                {/* CANCELED or INCOMPLETE_EXPIRED - show reactivate button */}
                                {(accountInfo.stripeSubscriptionStatus ===
                                    StripeSubscriptionStatus.CANCELED ||
                                    accountInfo.stripeSubscriptionStatus ===
                                        StripeSubscriptionStatus.INCOMPLETE_EXPIRED) && (
                                    <>
                                        <button
                                            onClick={handleUpdatePayment}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Manage Payment
                                        </button>
                                        <button
                                            onClick={handleReactivateSubscription}
                                            disabled={subscriptionLoading}
                                            className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                        >
                                            {subscriptionLoading ? (
                                                <div className='flex items-center'>
                                                    <Spinner
                                                        size='sm'
                                                        color='black'
                                                        className='mr-2'
                                                    />
                                                    Reactivating...
                                                </div>
                                            ) : (
                                                'Reactivate'
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='text-center py-8'>
                            <div className='text-gray-400 mb-4'>
                                <svg
                                    className='w-12 h-12 mx-auto'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                                    ></path>
                                </svg>
                            </div>
                            <p className='text-gray-500 mb-4'>
                                Subscription information not available
                            </p>
                            <button
                                onClick={loadAccountInfo}
                                className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                            >
                                Load Subscription Info
                            </button>
                        </div>
                    )}

                    {/* Message for non-autorenewing active/trial subscriptions */}
                    {renderSubscriptionEndMessage()}
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
                    {/* Account Information */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Account Information
                        </h2>

                        {/* Username success/error messages */}
                        {usernameSuccess && (
                            <div className='mb-6'>
                                <div className='p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg'>
                                    <div className='flex items-center'>
                                        <svg
                                            className='w-5 h-5 mr-2'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth='2'
                                                d='M5 13l4 4L19 7'
                                            ></path>
                                        </svg>
                                        {usernameSuccess}
                                    </div>
                                </div>
                            </div>
                        )}

                        {usernameError && (
                            <div className='mb-6'>
                                <div className='p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                                    <div className='flex items-center'>
                                        <svg
                                            className='w-5 h-5 mr-2'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth='2'
                                                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                            ></path>
                                        </svg>
                                        {usernameError}
                                    </div>
                                </div>
                            </div>
                        )}

                        {accountInfo ? (
                            <div className='flex flex-col flex-grow space-y-4'>
                                {/* Email (read-only) */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Email Address
                                    </label>
                                    <input
                                        type='email'
                                        value={accountInfo.email}
                                        disabled
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed'
                                    />
                                </div>

                                {/* Username Update */}
                                <form
                                    onSubmit={handleUsernameUpdate}
                                    className='flex flex-col flex-grow space-y-4'
                                >
                                    <div>
                                        <label
                                            htmlFor='username'
                                            className='block text-sm font-medium text-gray-700 mb-2'
                                        >
                                            Username
                                        </label>
                                        <input
                                            type='text'
                                            id='username'
                                            value={newUsername}
                                            onChange={handleUsernameChange}
                                            disabled={usernameLoading}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed ${
                                                newUsername.trim() && !usernameValidation.isValid
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : newUsername.trim() &&
                                                      usernameValidation.isValid
                                                    ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                            }`}
                                            placeholder='Choose a username (3-20 chars)'
                                        />
                                        <p className='mt-2 text-sm text-gray-500'>
                                            Allowed characters: letters, numbers, underscores (_),
                                            and hyphens (-)
                                        </p>
                                        {/* Username validation message */}
                                        {newUsername.trim() && !usernameValidation.isValid && (
                                            <p className='mt-1 text-sm text-red-600'>
                                                {usernameValidation.error}
                                            </p>
                                        )}
                                    </div>
                                    <div className='flex-grow'></div>
                                    <div>
                                        <button
                                            type='submit'
                                            disabled={
                                                usernameLoading ||
                                                newUsername === accountInfo.username ||
                                                !usernameValidation.isValid
                                            }
                                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                        >
                                            {usernameLoading ? (
                                                <div className='flex items-center justify-center'>
                                                    <Spinner
                                                        size='sm'
                                                        color='black'
                                                        className='mr-2'
                                                    />
                                                    Updating...
                                                </div>
                                            ) : (
                                                'Update Username'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className='text-center py-8'>
                                <div className='text-gray-400 mb-4'>
                                    <svg
                                        className='w-12 h-12 mx-auto'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                                        ></path>
                                    </svg>
                                </div>
                                <p className='text-gray-500 mb-4'>
                                    Account information not available
                                </p>
                                <button
                                    onClick={loadAccountInfo}
                                    className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                >
                                    Load Account Info
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Password Change */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Change Password
                        </h2>

                        {/* Password success/error messages */}
                        {passwordSuccess && (
                            <div className='mb-6'>
                                <div className='p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg'>
                                    <div className='flex items-center'>
                                        <svg
                                            className='w-5 h-5 mr-2'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth='2'
                                                d='M5 13l4 4L19 7'
                                            ></path>
                                        </svg>
                                        {passwordSuccess}
                                    </div>
                                </div>
                            </div>
                        )}

                        {passwordError && (
                            <div className='mb-6'>
                                <div className='p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                                    <div className='flex items-center'>
                                        <svg
                                            className='w-5 h-5 mr-2'
                                            fill='none'
                                            stroke='currentColor'
                                            viewBox='0 0 24 24'
                                        >
                                            <path
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                strokeWidth='2'
                                                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                            ></path>
                                        </svg>
                                        {passwordError}
                                    </div>
                                </div>
                            </div>
                        )}

                        <form
                            onSubmit={handlePasswordChange}
                            className='flex flex-col flex-grow space-y-4'
                        >
                            <div>
                                <label
                                    htmlFor='currentPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Current Password
                                </label>
                                <input
                                    type='password'
                                    id='currentPassword'
                                    value={currentPassword}
                                    onChange={e => {
                                        setCurrentPassword(e.target.value);
                                        setPasswordError('');
                                        setPasswordSuccess('');
                                    }}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Enter current password'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='newPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    New Password
                                </label>
                                <input
                                    type='password'
                                    id='newPassword'
                                    value={newPassword}
                                    onChange={e => {
                                        setNewPassword(e.target.value);
                                        setPasswordError('');
                                        setPasswordSuccess('');
                                    }}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Enter new password'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='confirmPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Confirm New Password
                                </label>
                                <input
                                    type='password'
                                    id='confirmPassword'
                                    value={confirmPassword}
                                    onChange={e => {
                                        setConfirmPassword(e.target.value);
                                        setPasswordError('');
                                        setPasswordSuccess('');
                                    }}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Confirm new password'
                                />
                            </div>

                            <div className='flex-grow'></div>
                            <div>
                                <button
                                    type='submit'
                                    disabled={
                                        passwordLoading ||
                                        !currentPassword ||
                                        !newPassword ||
                                        !confirmPassword
                                    }
                                    className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                >
                                    {passwordLoading ? (
                                        <div className='flex items-center justify-center'>
                                            <Spinner size='sm' color='black' className='mr-2' />
                                            Changing...
                                        </div>
                                    ) : (
                                        'Change Password'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {showCancelModal && (
                    <div className='fixed inset-0 z-50 flex items-center justify-center'>
                        <div className='absolute inset-0 bg-black opacity-50'></div>
                        <div className='relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-full max-w-lg mx-4'>
                            <h3 className='text-xl font-semibold text-gray-900 mb-2'>
                                We're sorry to see you go
                            </h3>
                            <p className='text-sm text-gray-600 mb-4'>
                                Please tell us why you want to cancel (optional, max 500
                                characters).
                            </p>
                            <textarea
                                value={cancelReason}
                                onChange={e =>
                                    setCancelReason(e.target.value.slice(0, maxCancelReasonLength))
                                }
                                maxLength={maxCancelReasonLength}
                                rows={5}
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200'
                                placeholder='Your feedback helps us improve'
                            />
                            <div className='mt-2 text-xs text-gray-500 text-right'>
                                {cancelReason.length}/{maxCancelReasonLength}
                            </div>
                            <div className='mt-4 flex justify-end gap-3'>
                                <button
                                    onClick={() => setShowCancelModal(false)}
                                    disabled={subscriptionLoading}
                                    className='px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Never mind
                                </button>
                                <button
                                    onClick={confirmCancelSubscription}
                                    disabled={subscriptionLoading}
                                    className='bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                >
                                    {subscriptionLoading ? (
                                        <div className='flex items-center'>
                                            <Spinner size='sm' color='black' className='mr-2' />
                                            Cancelling...
                                        </div>
                                    ) : (
                                        'Confirm cancellation'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showReactivationModal && (
                    <div className='fixed inset-0 z-50 flex items-center justify-center'>
                        <div className='absolute inset-0 bg-black opacity-50'></div>
                        <div className='relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-full max-w-lg mx-4'>
                            {reactivationLoading && (
                                <>
                                    <h3 className='text-xl font-semibold text-gray-900 mb-4 text-center'>
                                        Reactivating Subscription
                                    </h3>
                                    <div className='flex flex-col items-center justify-center py-8'>
                                        <Spinner size='lg' color='blue' />
                                        <p className='mt-4 text-gray-600'>Please wait...</p>
                                    </div>
                                </>
                            )}

                            {reactivationSuccess && !reactivationLoading && (
                                <>
                                    <h3 className='text-xl font-semibold text-gray-900 mb-2 text-center'>
                                        Welcome back!
                                    </h3>
                                    <p className='text-sm text-gray-600 mb-6 text-center'>
                                        Your subscription has been reactivated successfully.
                                    </p>
                                    <div className='flex justify-center'>
                                        <button
                                            onClick={handleBackToPuzzlePlanet}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Back to Puzzle Planet
                                        </button>
                                    </div>
                                </>
                            )}

                            {reactivationError && !reactivationLoading && (
                                <>
                                    <h3 className='text-xl font-semibold text-gray-900 mb-2 text-center'>
                                        Reactivation Failed
                                    </h3>
                                    <p className='text-sm text-gray-600 mb-6 text-center'>
                                        We couldn't reactivate your subscription. Please
                                        re-subscribe to continue.
                                    </p>
                                    <div className='flex justify-center'>
                                        <button
                                            onClick={handleReSubscribe}
                                            className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Re-subscribe
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Back to Game Button */}
                <div className='mt-8 text-center'>
                    <button
                        onClick={() =>
                            (window.location.href = `${
                                import.meta.env.VITE_REACT_APP_GAME_URL
                            }?t=${new Date().getTime()}`)
                        }
                        className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-8 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                    >
                        Back to Puzzle Planet
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Account;
